import { useState, useEffect } from 'react'
import { useUIStore } from '@/stores/ui.store'
import { useIntegracoes, useDeleteIntegracao, useTestIntegracao } from '@/hooks/useIntegracoes'
import { IntegracaoForm } from '@/components/admin/IntegracaoForm'
import {
    Plus, Edit, Trash2, Play, Search, Loader2, Network
} from 'lucide-react'
import type { Integracao } from '@/types/database'

export function IntegracoesPage() {
    const setPageTitle = useUIStore((s) => s.setPageTitle)
    const [search, setSearch] = useState('')
    const [showForm, setShowForm] = useState(false)
    const [editing, setEditing] = useState<Integracao | null>(null)
    const [testingId, setTestingId] = useState<string | null>(null)

    useEffect(() => { setPageTitle('Integrações') }, [setPageTitle])

    const { data: integracoes, isLoading } = useIntegracoes()
    const deleteMut = useDeleteIntegracao()
    const testMut = useTestIntegracao()

    const filtered = integracoes?.filter(i =>
        i.nome.toLowerCase().includes(search.toLowerCase()) ||
        i.endpoint.toLowerCase().includes(search.toLowerCase())
    ) || []

    const handleDelete = async (id: string) => {
        if (confirm('Tem certeza que deseja remover esta integração?')) {
            await deleteMut.mutateAsync(id)
        }
    }

    const handleTest = async (id: string) => {
        setTestingId(id)
        try {
            const res = await testMut.mutateAsync({ id, payload: { test: true } })
            alert(`Teste realizado com sucesso!\nStatus: ${res.status}\nDuração: ${res.duration_ms}ms`)
        } catch (err) {
            alert('Erro ao realizar teste.')
            console.error(err)
        } finally {
            setTestingId(null)
        }
    }

    return (
        <div className="space-y-6 animate-fade-in">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h2 className="text-xl font-bold text-surface-900">Integrações</h2>
                    <p className="text-sm text-surface-700/60 mt-0.5">Gerencie webhooks e integrações externas</p>
                </div>
                <button onClick={() => { setEditing(null); setShowForm(true) }} className="btn btn-primary btn-md gap-2">
                    <Plus size={18} /> Nova Integração
                </button>
            </div>

            {/* Content */}
            <div className="glass-card">
                {/* Toolbar */}
                <div className="p-4 border-b border-surface-100 flex gap-4">
                    <div className="relative flex-1 max-w-md">
                        <Search size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-surface-700/40" />
                        <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
                            placeholder="Buscar por nome ou endpoint..."
                            className="input pl-10" />
                    </div>
                </div>

                {/* List */}
                {isLoading ? (
                    <div className="flex justify-center p-12">
                        <Loader2 size={24} className="animate-spin text-primary-600" />
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="text-center py-16 text-surface-700/60">
                        <Network size={40} className="mx-auto mb-3 opacity-20" />
                        <p>Nenhuma integração encontrada</p>
                    </div>
                ) : (
                    <div className="divide-y divide-surface-100">
                        {filtered.map(item => (
                            <div key={item.id} className="p-4 flex items-center justify-between hover:bg-surface-50 transition-colors">
                                <div className="flex items-start gap-4">
                                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0
                                        ${item.ativo ? 'bg-green-100 text-green-600' : 'bg-surface-100 text-surface-400'}`}>
                                        <Network size={20} />
                                    </div>
                                    <div>
                                        <div className="flex items-center gap-2">
                                            <h3 className="font-semibold text-surface-900">{item.nome}</h3>
                                            <span className={`text-[10px] uppercase font-bold px-1.5 py-0.5 rounded border
                                                ${item.tipo === 'webhook' ? 'bg-purple-50 text-purple-600 border-purple-100' : 'bg-blue-50 text-blue-600 border-blue-100'}`}>
                                                {item.tipo}
                                            </span>
                                            {item.ambiente === 'sandbox' && (
                                                <span className="text-[10px] uppercase font-bold px-1.5 py-0.5 rounded bg-yellow-50 text-yellow-700 border border-yellow-100">
                                                    Sandbox
                                                </span>
                                            )}
                                        </div>
                                        <p className="text-xs font-mono text-surface-700/60 mt-0.5 break-all max-w-md">
                                            <span className="font-bold mr-1">{item.method}</span>
                                            {item.endpoint}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <button onClick={() => handleTest(item.id)} disabled={!!testingId}
                                        className="btn btn-sm btn-ghost gap-1.5 text-surface-700 hover:text-primary-600"
                                        title="Testar Conexão">
                                        {testingId === item.id ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />}
                                        <span className="hidden sm:inline">Testar</span>
                                    </button>
                                    <div className="h-4 w-px bg-surface-200 mx-1" />
                                    <button onClick={() => { setEditing(item); setShowForm(true) }}
                                        className="p-1.5 rounded-lg hover:bg-surface-100 text-surface-700/60 hover:text-primary-600">
                                        <Edit size={16} />
                                    </button>
                                    <button onClick={() => handleDelete(item.id)}
                                        className="p-1.5 rounded-lg hover:bg-danger-50 text-surface-700/60 hover:text-danger-600">
                                        <Trash2 size={16} />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Form Modal */}
            {showForm && (
                <IntegracaoForm
                    integracao={editing}
                    onClose={() => { setShowForm(false); setEditing(null) }}
                    onSuccess={() => { setShowForm(false); setEditing(null) }}
                />
            )}
        </div>
    )
}
